import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Route } from 'react-router-dom'
import { dataTypes } from '@easybutton/constants'
import * as inflected from 'inflected'

import { buildIndex } from '../../../../utils/indexing'
import { formatValue } from '../../../../utils/formatting'
import { getViewActions } from '../../../../utils/actions'
import { getView, fetchView, fetchViews } from '../../../../ducks/views'
import { getTable, fetchDatabases } from '../../../../ducks/databases'
import { fetchViewDetailsData, getRecord } from '../../../../ducks/data'
import { fetchActions, getActions } from '../../../../ducks/actions'
import { getCurrentOrganizationId } from '../../../../ducks/users'

import Subnav from '../../../Shared/Subnav'
import Card from '../../../Shared/Card'
import { FullScreenLoader } from '../../../Shared/Loading'
import { PropertyList, Property } from '../../../Shared/PropertyList'
import ObjectExplorer from '../../../Shared/ObjectExplorer'
import Button from '../../../Shared/Button'
import Accordion from '../../../Shared/Accordion'
import EmptyState from '../../../Shared/EmptyState'
import ActionMenu, { showActionModal } from '../ActionMenu'
import ActionModal from '../List/ActionModal'
import { User, FormattedNumber, Attachments } from './Airtable'

import styles from './Details.module.css'

const isExpandable = prop => {
  return [dataTypes.ARRAY, dataTypes.OBJECT].includes(prop.dataType)
}

const Value = function ViewDetailsValue({ value, column = {} }) {
  if (value) {
    switch (column.dataType) {
      case dataTypes.AIRTABLE_USER:
        return <User user={value} />

      case dataTypes.AIRTABLE_USERS:
        return (
          <>
            {value && value.map(user => <User key={user.email} user={user} />)}
          </>
        )

      case dataTypes.AIRTABLE_ATTACHMENTS:
        return (
          <Attachments
            urls={value.map(a => a.thumbnails && a.thumbnails.small.url)}
          />
        )

      default:
        if (column.dataType === dataTypes.NUMBER && column.airtableDataType) {
          return <FormattedNumber value={value} column={column} />
        }
    }
  }

  return <ObjectExplorer object={value} column={column} />
}

const ExpandableValue = function ExpandableValue({ value, column }) {
  const label = column.displayName || column.name
  const isArray = Array.isArray(value)

  const empty = isArray
    ? value.length === 0
    : Object.keys(value).filter(k => ![null, undefined, ''].includes(value[k]))
        .length === 0

  return (
    <Accordion
      carret
      indented
      className={styles.expandable}
      title={
        <p className={styles.expandableTitle}>
          {inflected.titleize(inflected.humanize(inflected.underscore(label)))}
        </p>
      }
    >
      {() => {
        if (empty) {
          return (
            <EmptyState tight>
              <h4>No Items</h4>
            </EmptyState>
          )
        }

        if (isArray) {
          return (
            <PropertyList className={styles.expandableChildren}>
              {value.map((item, i) =>
                item && typeof item === 'object' ? (
                  <ExpandableValue
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    column={{ name: `Item ${i + 1}` }}
                    value={item}
                  />
                ) : (
                  // eslint-disable-next-line react/no-array-index-key
                  <Property key={i} label={`Item ${i + 1}`}>
                    <Value value={item} />
                  </Property>
                )
              )}
            </PropertyList>
          )
        }

        const allKeys = Object.keys(value).filter(
          k => ![null, undefined, ''].includes(value[k])
        )

        const expandableKeys = allKeys.filter(
          k => value[k] && typeof value[k] === 'object'
        )

        const simpleKeys = allKeys.filter(k => !expandableKeys.includes(k))

        return (
          <PropertyList className={styles.expandableChildren}>
            {simpleKeys.map(key => (
              <Property key={key} label={key}>
                <Value value={value[key]} />
              </Property>
            ))}
            {expandableKeys.map(key => (
              <ExpandableValue
                key={key}
                column={{ name: key }}
                value={value[key]}
              />
            ))}
          </PropertyList>
        )
      }}
    </Accordion>
  )
}

export default function ViewDetails({ match, history }) {
  let { viewId, recordId } = match.params

  const dispatch = useDispatch()

  const view = useSelector(getView(viewId))
  const record = useSelector(getRecord(viewId, recordId))
  const allActions = useSelector(getActions(view && view.OrganizationId))
  const organizationId = useSelector(getCurrentOrganizationId)

  const table = useSelector(
    getTable(
      view && view.body && view.body.databaseId,
      view && view.body && view.body.table
    )
  )

  // Fetch View
  useEffect(() => {
    dispatch(fetchViews(organizationId))
    dispatch(fetchView(viewId))
    dispatch(fetchDatabases(organizationId))
  }, [dispatch, viewId, organizationId])

  // Fetch Data (re-fetch after running action)
  useEffect(() => {
    dispatch(fetchViewDetailsData(viewId, recordId))
  }, [dispatch, viewId, recordId, history.location.pathname])

  // Fetch Actions
  useEffect(() => {
    if (view) {
      dispatch(fetchActions(view.OrganizationId))
    }
  }, [view && view.OrganizationId])

  if (!view || !record || !table) {
    return <FullScreenLoader />
  }

  const columnsMap = buildIndex(table.columns, col => col.name)

  const actions = getViewActions(view, allActions)

  const properties = (view.body.detailsFields || []).filter(
    itm => itm && itm.column
  )

  const [action1, action2] = actions[0].children

  const baseURL = `/databases/views/${view.id}/records/${recordId}`

  const handleClickAction = option => () => {
    showActionModal(option.value, {
      history,
      record,
      baseURL,
    })
  }

  const allProperties = properties.filter(
    prop =>
      ![null, undefined, ''].includes(record[prop.column]) &&
      columnsMap[prop.column]
  )

  const simpleProperties = allProperties.filter(
    prop => !isExpandable(columnsMap[prop.column])
  )

  const expandableProperties = allProperties.filter(prop =>
    isExpandable(columnsMap[prop.column])
  )

  return (
    <>
      <Subnav
        withBackButton
        title={
          formatValue(
            record[view.body.labelColumn],
            columnsMap[view.body.labelColumn]
          ) || 'Untitled'
        }
        actions={
          <>
            {action1 && (
              <Button onClick={handleClickAction(action1)}>
                {action1.label}
              </Button>
            )}
            {action2 && (
              <Button color="grey" onClick={handleClickAction(action2)}>
                {action2.label}
              </Button>
            )}
            <ActionMenu
              record={record}
              view={view}
              actions={actions}
              baseURL={baseURL}
            />
          </>
        }
      />
      <div className={styles.wrapper}>
        <Card>
          <h2>{`${inflected.titleize(
            table.singularName || table.name
          )} Details`}</h2>
          <PropertyList>
            {simpleProperties.map(prop =>
              [null, undefined, ''].includes(record[prop.column]) ||
              (Array.isArray(record[prop.column]) &&
                record[prop.column].length === 0) ||
              !columnsMap[prop.column] ? null : (
                <Property label={prop.label || prop.column}>
                  <Value
                    value={record[prop.column]}
                    column={columnsMap[prop.column]}
                  />
                </Property>
              )
            )}
          </PropertyList>
          <div className={styles.expandableWrapper}>
            {expandableProperties.map(prop =>
              [null, undefined, ''].includes(record[prop.column]) ||
              !columnsMap[prop.column] ? null : (
                <ExpandableValue
                  value={record[prop.column]}
                  column={columnsMap[prop.column]}
                />
              )
            )}
          </div>
        </Card>
      </div>
      <Route
        path="/databases/views/:viewId/records/:recordId/actions/:actionId"
        component={ActionModal}
      />
    </>
  )
}
